import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { enhancedApi } from '../api';
import SettingsPageLayout from './settings/SettingsPageLayout';
import { useSettingsForm } from './SettingsFormContext';

const ACCEPTED_LOGO_TYPES = ['image/png', 'image/svg+xml', 'image/jpeg', 'image/jpg'];
const MAX_LOGO_SIZE = 2 * 1024 * 1024;
const PREFS_STORAGE_KEY = 'beck_firm_display_settings';

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
  'UTC',
  'Europe/London',
];

const DATE_FORMATS = [
  { value: 'MM/dd/yyyy', label: 'MM/DD/YYYY' },
  { value: 'dd/MM/yyyy', label: 'DD/MM/YYYY' },
  { value: 'yyyy-MM-dd', label: 'YYYY-MM-DD' },
];

const US_STATES = [
  'Alabama',
  'Alaska',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'Florida',
  'Georgia',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
];

const toHex = (val) => {
  if (!val || typeof val !== 'string') return '#1a38ff';
  const s = val.startsWith('#') ? val : `#${val}`;
  return /^#[0-9A-Fa-f]{6}$/.test(s) ? s : '#1a38ff';
};

function CompanyInformation() {
  const { setIsDirty } = useSettingsForm();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [firmName, setFirmName] = useState('');
  const [website, setWebsite] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [stateVal, setStateVal] = useState('');
  const [zip, setZip] = useState('');
  const [timezone, setTimezone] = useState('America/New_York');
  const [dateFormat, setDateFormat] = useState('MM/dd/yyyy');

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [brandingMeta, setBrandingMeta] = useState({
    primary_color: '#1a38ff',
    secondary_color: '#dc004e',
    company_display_name: '',
    custom_domain: '',
  });

  const fetchData = useCallback(async ({ withSpinner = true } = {}) => {
    if (withSpinner) {
      setLoading(true);
    }
    setError('');
    try {
      const [coRes, brandRes] = await Promise.all([
        enhancedApi.getCompanyInformation(),
        enhancedApi.getBranding().catch(() => ({ data: {} })),
      ]);

      let prefs = {};
      try {
        prefs = JSON.parse(localStorage.getItem(PREFS_STORAGE_KEY) || '{}');
      } catch {
        prefs = {};
      }

      if (coRes.data?.success && coRes.data.data) {
        const c = coRes.data.data;
        setFirmName(c.name || '');
        setWebsite(c.website || '');
        setPhone(c.phone || '');
        setAddress(c.address || '');
        setCity(c.city || '');
        setStateVal(c.state || '');
        setZip(c.zip_code || '');
      }

      const b = brandRes.data?.data ?? brandRes.data ?? {};
      setBrandingMeta({
        primary_color: toHex(b.primary_color || '#1a38ff'),
        secondary_color: toHex(b.secondary_color || '#dc004e'),
        company_display_name: b.company_display_name || '',
        custom_domain: b.custom_domain || '',
      });
      if (b.logo_url) {
        setLogoPreview(b.logo_url);
      } else {
        setLogoPreview(null);
      }
      setLogoFile(null);

      if (prefs.timezone) setTimezone(prefs.timezone);
      if (prefs.dateFormat) setDateFormat(prefs.dateFormat);
    } catch {
      setError('Failed to load company profile');
    } finally {
      if (withSpinner) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchData({ withSpinner: true });
  }, [fetchData]);

  const dirty = () => setIsDirty(true);

  const applyLogoFile = (file) => {
    if (!file) return;
    if (!ACCEPTED_LOGO_TYPES.includes(file.type)) {
      setError('Logo must be PNG, SVG, or JPG');
      return;
    }
    if (file.size > MAX_LOGO_SIZE) {
      setError('Logo must be under 2MB');
      return;
    }
    setError('');
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    dirty();
  };

  const onFileInput = (e) => {
    const f = e.target.files?.[0];
    if (f) applyLogoFile(f);
    e.target.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const f = e.dataTransfer.files?.[0];
    if (f) applyLogoFile(f);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const apiData = {
        name: firmName,
        website,
        phone,
        address,
        city,
        state: stateVal,
        zip_code: zip,
      };
      const res = await enhancedApi.updateCompanyInformation(apiData);
      if (!res.data?.success) {
        throw new Error(res.data?.error || 'Save failed');
      }

      localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify({ timezone, dateFormat }));

      if (logoFile) {
        const fd = new FormData();
        fd.append('logo', logoFile);
        fd.append('primary_color', brandingMeta.primary_color);
        fd.append('secondary_color', brandingMeta.secondary_color);
        fd.append('company_display_name', brandingMeta.company_display_name || firmName);
        fd.append('custom_domain', brandingMeta.custom_domain || '');
        await enhancedApi.updateBranding(fd);
        setLogoFile(null);
      }

      setIsDirty(false);
      setSnackbar({ open: true, message: 'Company profile saved', severity: 'success' });
      await fetchData({ withSpinner: false });
    } catch {
      setSnackbar({ open: true, message: 'Could not save company profile', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsDirty(false);
    fetchData({ withSpinner: true });
  };

  if (loading) {
    return (
      <SettingsPageLayout title="Company Profile" description="Legal name, location, and how your firm appears.">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </SettingsPageLayout>
    );
  }

  return (
    <>
      <SettingsPageLayout
        title="Company Profile"
        description="Firm details used on documents, invoices, and the client portal. Timezone and date format apply to how dates display for your user on this device."
        onSave={handleSave}
        onCancel={handleCancel}
        saving={saving}
      >
        {error ? (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        ) : null}

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Firm name"
              value={firmName}
              onChange={(e) => {
                setFirmName(e.target.value);
                dirty();
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Website"
              value={website}
              onChange={(e) => {
                setWebsite(e.target.value);
                dirty();
              }}
              placeholder="https://"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Phone"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                dirty();
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address"
              value={address}
              onChange={(e) => {
                setAddress(e.target.value);
                dirty();
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="City"
              value={city}
              onChange={(e) => {
                setCity(e.target.value);
                dirty();
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="co-state-label">State</InputLabel>
              <Select
                labelId="co-state-label"
                label="State"
                value={stateVal}
                onChange={(e) => {
                  setStateVal(e.target.value);
                  dirty();
                }}
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {US_STATES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="ZIP"
              value={zip}
              onChange={(e) => {
                setZip(e.target.value);
                dirty();
              }}
            />
          </Grid>
        </Grid>

        <Box>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Firm logo
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            PNG, SVG, or JPG · max 2MB. Also used for client portal branding.
          </Typography>
          <input ref={fileInputRef} type="file" accept={ACCEPTED_LOGO_TYPES.join(',')} hidden onChange={onFileInput} />
          <Box
            onDrop={onDrop}
            onDragOver={onDragOver}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: 'action.hover',
              transition: 'border-color 0.2s, background-color 0.2s',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: (theme) => theme.palette.action.selected,
              },
            }}
          >
            {logoPreview ? (
              <Box
                component="img"
                src={logoPreview}
                alt="Firm logo preview"
                sx={{ maxHeight: 120, maxWidth: '100%', objectFit: 'contain' }}
              />
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Drag and drop an image here, or click to browse
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="tz-label">Timezone</InputLabel>
              <Select
                labelId="tz-label"
                label="Timezone"
                value={timezone}
                onChange={(e) => {
                  setTimezone(e.target.value);
                  dirty();
                }}
              >
                {TIMEZONES.map((tz) => (
                  <MenuItem key={tz} value={tz}>
                    {tz.replace(/_/g, ' ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="df-label">Date format</InputLabel>
              <Select
                labelId="df-label"
                label="Date format"
                value={dateFormat}
                onChange={(e) => {
                  setDateFormat(e.target.value);
                  dirty();
                }}
              >
                {DATE_FORMATS.map((df) => (
                  <MenuItem key={df.value} value={df.value}>
                    {df.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </SettingsPageLayout>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default CompanyInformation;
